<template>
  <!-- Mobile Date Input - Apply uniform styles -->
  <input
    v-if="isMobile"
    :id="fieldId"
    ref="nativeInput"
    type="date"
    :value="modelValue"
    @input="$emit('update:modelValue', $event.target.value)"
    :min="minDate"
    :max="maxDate"
    :class="[
      'w-full h-10 px-3 py-2 text-base sm:text-sm border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2',
      error ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500' : 'border-gray-300',
      !error ? (
        props.userRole === 'customer' ? 'focus:ring-green-500 focus:border-green-500' : 'focus:ring-primary-500 focus:border-primary-500'
      ) : ''
    ]"
    :disabled="disabled"
    :required="required"
  />

  <!-- Desktop Date Picker using InputWithIcon -->
  <!-- This div is necessary to hold both InputWithIcon and the absolute positioned calendar -->
  <div v-else class="relative w-full">
    <InputWithIcon 
      icon="mdi:calendar" 
      :disabled="disabled"
      :icon-click-handler="toggleCalendar"
      :userRole="userRole"
      :hasError="!!error"
    >
      <template #default="{ hasIcon }">
        <input
          :id="fieldId" 
          ref="displayInput"
          type="text"
          :value="formattedDate"
          placeholder="Select a date..."
          :class="[
            'w-full h-full px-3 py-2 text-base sm:text-sm bg-transparent focus:outline-none focus:ring-0 focus:border-none',
            { 
              'rounded-r-md': true,
              'rounded-l-none': hasIcon
            }
          ]"
          :disabled="disabled"
          :required="required"
          readonly
          @click="!disabled && toggleCalendar()"
        />
      </template>
    </InputWithIcon>
      
    <!-- Custom Date Picker Dropdown -->
    <transition name="fade">
      <!-- 
        This dropdown needs to be positioned relative to its container.
        The new surrounding div with class="relative w-full" will serve as this context.
      -->
      <div 
        v-if="showCalendar && !disabled" 
        class="absolute z-10 mt-1 bg-white rounded-md shadow-lg p-4 border border-gray-200 w-full"
      >
        <!-- Month Navigation -->
        <div class="flex justify-between items-center mb-4">
          <button 
            type="button" 
            class="p-1 rounded-full hover:bg-gray-100" 
            @click="previousMonth"
          >
            <Icon icon="mdi:chevron-left" />
          </button>
          <div class="font-medium">{{ currentMonthName }} {{ currentYear }}</div>
          <button 
            type="button" 
            class="p-1 rounded-full hover:bg-gray-100" 
            @click="nextMonth"
          >
            <Icon icon="mdi:chevron-right" />
          </button>
        </div>
        
        <!-- Weekday Headers -->
        <div class="grid grid-cols-7 gap-1 text-center text-xs text-gray-500 mb-1">
          <div v-for="day in weekdayNames" :key="day">{{ day }}</div>
        </div>
        
        <!-- Date Grid -->
        <div class="grid grid-cols-7 gap-1">
          <!-- Empty cells for days before month starts -->
          <div 
            v-for="n in startDay" 
            :key="`empty-${n}`" 
            class="h-8 w-8"
          ></div>
          
          <!-- Actual days of the month -->
          <button
            v-for="day in daysInMonth"
            :key="day"
            type="button"
            :class="[
              'h-8 w-8 rounded-full flex items-center justify-center text-sm',
              isToday(day) ? (props.userRole === 'customer' ? 'border border-green-500' : 'border border-primary-500') : '',
              isSelected(day) ? 
                (props.userRole === 'customer' ? 'bg-green-500 text-white' : 'bg-primary-500 text-white') : 
                'hover:bg-gray-100',
              !isDateSelectable(day) ? 'text-gray-300 cursor-not-allowed' : ''
            ]"
            @click="selectDate(day)"
            :disabled="!isDateSelectable(day)"
          >
            {{ day }}
          </button>
        </div>
        
        <!-- Today Button -->
        <div class="mt-4 text-right">
          <button 
            type="button"
            :class="[
              'text-xs',
              props.userRole === 'customer' ? 'text-green-600 hover:text-green-700' : 'text-primary-600 hover:text-primary-700'
            ]"
            @click="selectToday"
            :disabled="!isTodaySelectable"
          >
            Today
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { Icon } from '@iconify/vue';
import InputWithIcon from '@/components/common/InputWithIcon.vue';
import { useMediaQuery } from '@vueuse/core';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    required: true
  },
  fieldId: {
    type: String,
    default: () => `date-${Math.random().toString(36).substring(2, 9)}`
  },
  minDate: {
    type: String,
    default: ''
  },
  maxDate: {
    type: String,
    default: ''
  },
  required: {
    type: Boolean,
    default: false
  },
  isPrimary: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: ''
  },
  helpText: {
    type: String,
    default: ''
  },
  description: {
    type: String,
    default: ''
  },
  example: {
    type: String,
    default: ''
  },
  displayFormat: {
    type: String,
    default: 'MM/DD/YYYY'
  },
  showOptionalLabel: {
    type: Boolean,
    default: true
  },
  showRequiredLabel: {
    type: Boolean,
    default: true
  },
  userRole: {
    type: String,
    default: 'lender', // 'lender' uses primary-xxx classes, 'customer' uses green-xxx
    validator: (value) => ['customer', 'lender'].includes(value)
  }
});

const emit = defineEmits(['update:modelValue']);

// Check if user is on mobile device
const isMobile = useMediaQuery('(max-width: 768px)');

// Date picker state
const showCalendar = ref(false);
const displayInput = ref(null);
const nativeInput = ref(null);

// Initialize currentDate carefully to avoid UTC interpretation of YYYY-MM-DD
const currentDate = ref(new Date()); // Default to now (local)

if (props.modelValue) {
  const parts = props.modelValue.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const monthIndex = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
    const day = parseInt(parts[2], 10);
    const localDate = new Date(year, monthIndex, day);
    if (!isNaN(localDate.getTime())) {
      currentDate.value = localDate;
    } else {
      console.warn(`[ResponsiveDateInput] Invalid date from modelValue after parsing: ${props.modelValue}. Defaulting to current browser date.`);
      currentDate.value = new Date();
    }
  } else if (props.modelValue !== '') { // only warn if not an empty string
    console.warn(`[ResponsiveDateInput] modelValue is not in YYYY-MM-DD format: ${props.modelValue}. Defaulting to current browser date.`);
    currentDate.value = new Date();
  }
} else {
  // modelValue is empty, default to current browser date for calendar starting view
  currentDate.value = new Date(); 
}

// Format display date for the input field
const formattedDate = computed(() => {
  if (!props.modelValue) return '';
  const parts = props.modelValue.split('-');
  if (parts.length !== 3) {
    // If modelValue is not in YYYY-MM-DD, return it as is or an empty string
    // This might happen if validation allows other formats initially
    return props.modelValue; // Or consider returning '' or an error indication
  }

  const year = parseInt(parts[0], 10);
  const monthIndex = parseInt(parts[1], 10) - 1;
  const dayValue = parseInt(parts[2], 10);

  // Create date as local for display formatting purposes
  const dateForDisplay = new Date(year, monthIndex, dayValue);
  if (isNaN(dateForDisplay.getTime())) {
    return props.modelValue; // Or '' or error indication
  }

  const displayMonth = (dateForDisplay.getMonth() + 1).toString().padStart(2, '0');
  const displayDay = dateForDisplay.getDate().toString().padStart(2, '0');
  const displayYear = dateForDisplay.getFullYear();
  
  if (props.displayFormat === 'MM/DD/YYYY') {
    return `${displayMonth}/${displayDay}/${displayYear}`;
  } else if (props.displayFormat === 'DD/MM/YYYY') {
    return `${displayDay}/${displayMonth}/${displayYear}`;
  } else if (props.displayFormat === 'YYYY-MM-DD') {
    return `${displayYear}-${displayMonth}-${displayDay}`;
  }
  
  return `${displayMonth}/${displayDay}/${displayYear}`; // Default to MM/DD/YYYY
});

// Calendar Utilities
const currentYear = computed(() => currentDate.value.getFullYear());
const currentMonth = computed(() => currentDate.value.getMonth());

const currentMonthName = computed(() => {
  const options = { month: 'long' };
  return new Intl.DateTimeFormat('en-US', options).format(currentDate.value);
});

const daysInMonth = computed(() => {
  const year = currentYear.value;
  const month = currentMonth.value;
  return new Date(year, month + 1, 0).getDate();
});

const startDay = computed(() => {
  const year = currentYear.value;
  const month = currentMonth.value;
  return new Date(year, month, 1).getDay();
});

const weekdayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// Navigation methods
const previousMonth = () => {
  const newDate = new Date(currentDate.value);
  newDate.setMonth(newDate.getMonth() - 1);
  currentDate.value = newDate;
};

const nextMonth = () => {
  const newDate = new Date(currentDate.value);
  newDate.setMonth(newDate.getMonth() + 1);
  currentDate.value = newDate;
};

// Selection methods
const isSelected = (day) => {
  if (!props.modelValue) return false;
  
  // Parse modelValue as local date to avoid UTC interpretation
  const parts = props.modelValue.split('-');
  if (parts.length !== 3) return false;
  
  const year = parseInt(parts[0], 10);
  const monthIndex = parseInt(parts[1], 10) - 1;
  const dayValue = parseInt(parts[2], 10);
  
  const date = new Date(year, monthIndex, dayValue);
  if (isNaN(date.getTime())) return false;
  
  return (
    date.getDate() === day &&
    date.getMonth() === currentMonth.value &&
    date.getFullYear() === currentYear.value
  );
};

const isToday = (day) => {
  const today = new Date();
  return (
    today.getDate() === day &&
    today.getMonth() === currentMonth.value &&
    today.getFullYear() === currentYear.value
  );
};

const selectDate = (day) => {
  const newDate = new Date(currentYear.value, currentMonth.value, day);
  const formattedValue = newDate.toISOString().split('T')[0]; // YYYY-MM-DD
  emit('update:modelValue', formattedValue);
  showCalendar.value = false;
};

const selectToday = () => {
  const today = new Date();
  currentDate.value = today;
  const formattedValue = today.toISOString().split('T')[0];
  emit('update:modelValue', formattedValue);
  showCalendar.value = false;
};

// Handle toggling of calendar visibility
const toggleCalendar = () => {
  showCalendar.value = !showCalendar.value;
};

// Click outside to close
const handleClickOutside = (event) => {
  const path = event.composedPath && event.composedPath();
  // The `InputWithIcon` component itself is now the main element for the desktop datepicker.
  // We need a ref to it if we are to use `closest` or `contains` accurately.
  // Let's assume `displayInput`'s parent is good enough for `closest('.relative')`
  const datePickerElement = displayInput.value?.closest('.relative'); // This targets the new div wrapper
  const calendarElement = document.querySelector('div.absolute.z-10[v-if="showCalendar && !disabled"]'); // More specific selector for calendar

  let clickedOutside = true;
  if (path) {
    if (datePickerElement && path.includes(datePickerElement)) clickedOutside = false;
    if (calendarElement && path.includes(calendarElement)) clickedOutside = false;
  } else {
    if (datePickerElement && datePickerElement.contains(event.target)) clickedOutside = false;
    if (calendarElement && calendarElement.contains(event.target)) clickedOutside = false;
  }
  if (showCalendar.value && clickedOutside) {
    showCalendar.value = false;
  }
};

// Date constraints
const isDateSelectable = (day) => {
  const date = new Date(currentYear.value, currentMonth.value, day);
  const dateStr = date.toISOString().split('T')[0];
  
  if (props.minDate && dateStr < props.minDate) return false;
  if (props.maxDate && dateStr > props.maxDate) return false;
  
  return true;
};

const isTodaySelectable = computed(() => {
  const today = new Date().toISOString().split('T')[0];
  
  if (props.minDate && today < props.minDate) return false;
  if (props.maxDate && today > props.maxDate) return false;
  
  return true;
});

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('click', handleClickOutside, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside, true);
});

// Expose methods for parent component
defineExpose({
  focus: () => {
    if (isMobile.value) nativeInput.value?.focus();
    else if (displayInput.value) displayInput.value.focus();
  }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style> 